// web/src/features/profile/hooks/useFamilyMembers.ts
//
// Loads a single player's family contacts for the Profile Family surface.
// Web port of the mobile hook; framework-agnostic logic over shared services.

import { useQuery } from '@tanstack/react-query'
import { queryKeys } from '@sportninja/common/config/queryKeys'
import {
  groupForRelationship,
  isSelfRelationship,
  relationshipLabel,
} from '@sportninja/common/features/profile/family/utils'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import {
  listPlayerFamily,
  type PlayerFamilyMember,
  type PlayerFamilyRelationshipGroup,
} from '@sportninja/common/services/player/listPlayerFamily'

export type FamilyRelationshipGroup = PlayerFamilyRelationshipGroup

export type FamilyMember = {
  id: string
  /** Combined display name for the row. */
  name: string
  /** Split names for the edit form's First/Last Name fields. */
  nameFirst: string
  nameLast: string
  /** Saved relationship_type id (preferred for edit-form prefill). */
  relationshipId: number | null
  relationshipLabel: string
  group: FamilyRelationshipGroup
  email?: string | null
  phone?: string | null
  /** Whether the contact has administrative access to the player. */
  isPrimary: boolean
  /** Invitation status string (e.g. 'accepted', 'pending'). */
  status?: string | null
  /** True once the contact accepted — drives the green/yellow status dot. */
  isAccepted: boolean
}

export type FamilyGroup = {
  group: FamilyRelationshipGroup
  title: string
  members: FamilyMember[]
}

const GROUP_TITLES: Record<FamilyRelationshipGroup, string> = {
  parents_guardians: capitalizeWords('Parents / Guardians'),
  additional_contacts: capitalizeWords('Additional Contacts'),
}

const STALE_TIME = 5 * 60 * 1000

const memberName = (m: PlayerFamilyMember): string =>
  m.full_name || `${m.name_first ?? ''} ${m.name_last ?? ''}`.trim() || '—'

const toFamilyMember = (m: PlayerFamilyMember): FamilyMember => ({
  id: m.id,
  name: memberName(m),
  nameFirst: m.name_first ?? '',
  nameLast: m.name_last ?? '',
  relationshipId: m.relationship_type?.id ?? null,
  relationshipLabel: relationshipLabel(m.relationship_type),
  group: groupForRelationship(m.relationship_type),
  email: m.email,
  phone: m.phone_number,
  isPrimary: Boolean(m.is_primary),
  status: m.status,
  isAccepted: Boolean(m.is_accepted),
})

export const useFamilyMembers = (
  playerId: string | null | undefined
): {
  groups: FamilyGroup[]
  isLoading: boolean
  error: Error | null
  refetch: () => void
} => {
  const { data, isPending, isFetching, error, refetch } = useQuery({
    queryKey: queryKeys.playerFamily.byPlayer(playerId),
    queryFn: () => listPlayerFamily(playerId as string),
    enabled: Boolean(playerId),
    staleTime: STALE_TIME,
  })

  // The endpoint returns a flat member array (no wrapping player object). The
  // "Self" contact (the player themselves) is excluded from the list.
  const members: FamilyMember[] = (data ?? [])
    .filter((m) => !isSelfRelationship(m.relationship_type))
    .map(toFamilyMember)

  const order: FamilyRelationshipGroup[] = [
    'parents_guardians',
    'additional_contacts',
  ]
  const groups: FamilyGroup[] = order
    .map((group) => ({
      group,
      title: GROUP_TITLES[group],
      members: members.filter((m) => m.group === group),
    }))
    .filter((g) => g.members.length > 0)

  const isLoading = Boolean(playerId) && (isPending || isFetching)

  return { groups, isLoading, error: (error as Error) ?? null, refetch }
}
